import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Sparkles } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

interface Props {
  sessionId: string;
  onAccepted: (plan: string) => void;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function PlanGenerator({ sessionId, onAccepted }: Props) {
  const [generating, setGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [basedOnDate, setBasedOnDate] = useState<string | null>(null);
  const [reason, setReason] = useState<string | null>(null);

  async function generate() {
    setGenerating(true);
    setReason(null);
    try {
      const result = await api.coach.sessions.generatePlan(sessionId);
      if (result.suggestion === null) {
        setReason(result.reason ?? 'no_prior_debrief');
        setSuggestion(null);
      } else {
        setSuggestion(result.suggestion);
        if (result.basedOnSessionId) {
          try {
            const prior = await api.coach.sessions.get(result.basedOnSessionId);
            setBasedOnDate(prior.date);
          } catch {
            setBasedOnDate(null);
          }
        }
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Fahrplan-Generierung fehlgeschlagen.');
    } finally {
      setGenerating(false);
    }
  }

  if (suggestion !== null) {
    return (
      <div className="rounded-xl border border-blue-300 bg-blue-50/50 p-4 space-y-3">
        <p className="text-xs uppercase font-medium text-blue-900">Fahrplan-Vorschlag</p>
        <textarea
          value={suggestion}
          onChange={(e) => setSuggestion(e.target.value)}
          className="w-full rounded-md border border-blue-200 bg-white p-3 text-sm min-h-[100px] resize-y"
        />
        {basedOnDate && (
          <p className="text-xs text-muted-foreground">
            Basierend auf Session vom {formatDate(basedOnDate)}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => { setSuggestion(null); setBasedOnDate(null); }}>
            Verwerfen
          </Button>
          <Button size="sm" onClick={() => onAccepted(suggestion)}>
            Übernehmen
          </Button>
        </div>
      </div>
    );
  }

  if (reason === 'no_prior_debrief') {
    return (
      <p className="text-xs text-muted-foreground">
        Noch kein vorheriger Debrief vorhanden. Schreibe den Fahrplan manuell oder lege erst eine vorherige Session an.
      </p>
    );
  }

  return (
    <Button size="sm" variant="outline" onClick={generate} disabled={generating} className="gap-1.5">
      <Sparkles size={14} /> {generating ? 'Generiere…' : 'Fahrplan aus letzter Session generieren'}
    </Button>
  );
}
